import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const Header = ({ onBookNow }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "How It Works", href: "#how-it-works" },
    { name: "Services", href: "#services" },
    { name: "Fleet", href: "#fleet" },
    { name: "Contact", href: "#contact" },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header className="sarathi-header fixed top-0 w-full z-50 bg-sarathi-background/80 backdrop-blur-xl border-b border-white/5 transition-[top] duration-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo + BETA pill */}
          <a href="/" className="flex items-center gap-2" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_ev-mobility-2/artifacts/46iqyr35_logo-orange-white.svg"
              alt="Sarathi-X"
              className="h-8 sm:h-10"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                type="button"
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="text-sarathi-text-secondary hover:text-white transition-colors font-medium"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="/my-trips"
              className="text-sarathi-text-secondary hover:text-white transition-colors font-medium text-sm"
              data-testid="header-my-trips-link"
            >
              My Trips
            </a>
            <Button
              onClick={onBookNow}
              className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white font-semibold px-6 rounded-full"
              data-testid="header-book-btn"
            >
              Book a Ride
            </Button>
          </div>

          {/* Mobile: My Trips + Menu */}
          <div className="md:hidden flex items-center gap-3">
            <a
              href="/my-trips"
              className="text-sarathi-text-secondary hover:text-white text-sm font-medium transition-colors"
              data-testid="mobile-my-trips-quick-link"
            >
              My Trips
            </a>
            <button
              type="button"
              className="text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-sarathi-surface border-t border-white/5"
          >
            <div className="px-6 py-4 space-y-3">
              {navLinks.map((link) => (
                <button
                  type="button"
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left text-sarathi-text-secondary hover:text-white transition-colors py-2 font-medium"
                >
                  {link.name}
                </button>
              ))}
              <a
                href="/my-trips"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-sarathi-text-secondary hover:text-white transition-colors py-2 font-medium"
                data-testid="mobile-my-trips-link"
              >
                My Trips
              </a>
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onBookNow();
                }}
                className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white font-semibold rounded-full mt-4"
                data-testid="mobile-book-btn"
              >
                Book a Ride
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
